import React, { useEffect, useCallback, useRef } from "react";
import { ChevronDown, History } from "lucide-react";
import { ConversationItem } from "~/components/assistant/ConversationItem";
import { FeaturedConversationItem } from "~/components/assistant/FeaturedConversationItem";
import { IconButton, PopoverButton } from "~/components/assistant/PopoverButton";
import { useListboxKeyboardNavigation } from "~/components/assistant/hooks/useListboxKeyboardNavigation";
import { Conversation, ConversationShortcut } from "~/components/assistant/types";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useConversations } from "~/contexts/conversations";

// Extract conversation ID - defined outside component for stable reference
const getConversationId = (conversation: Conversation) => conversation.id;

interface ConversationSelectorProps {
  conversations: Conversation[];
  currentConversationId: string | null;
  conversationShortcuts?: ConversationShortcut[];
  isCreatingConversation?: boolean;
  /** When provided, overrides V1 context's isStreaming for each conversation. */
  isStreaming?: (conversationId: string) => boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
  /** Render as a compact icon button instead of showing the conversation name */
  iconOnly?: boolean;
}

export function ConversationSelector({
  conversations,
  currentConversationId,
  conversationShortcuts,
  isCreatingConversation = false,
  isStreaming: isStreamingProp,
  onSelect,
  onDelete,
  onRename,
  iconOnly = false,
}: ConversationSelectorProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const visibleShortcuts = conversationShortcuts?.filter((s) => s.url) ?? [];
  const conversationsContext = useConversations();
  const currentConversation = conversations.find((c) => c.id === currentConversationId);
  const lastKnownLabelRef = useRef<string | null>(null);

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      onSelect(conversationId);
      setIsOpen(false);
    },
    [onSelect]
  );

  const { focusedIndex, handleKeyDown, getFocusedItemId } = useListboxKeyboardNavigation({
    items: conversations,
    onSelect: (conversation) => handleConversationSelect(conversation.id),
    selectedItem: currentConversation || null,
    getItemId: getConversationId,
    idPrefix: "conversation-selector-",
  });

  const formatConversationLabel = (conversation: Conversation) => {
    // Use display_name if available
    if (conversation.display_name) {
      return conversation.display_name;
    }

    const date = new Date(conversation.created_at);
    const now = new Date();

    const timeString = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // Check if it's today, yesterday, or another date
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = (() => {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return date.toDateString() === yesterday.toDateString();
    })();

    let dateString: string;

    if (isToday) {
      dateString = "Today";
    } else if (isYesterday) {
      dateString = "Yesterday";
    } else {
      dateString = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }

    return `${dateString}, ${timeString}`;
  };

  if (currentConversation) {
    lastKnownLabelRef.current = formatConversationLabel(currentConversation);
  }

  useEffect(() => {
    if (conversations.length === 0) {
      setIsOpen(false);
    }
  }, [conversations]);

  const focusedItemId = getFocusedItemId();

  if (conversations.length === 0) {
    return null;
  }

  const trigger = iconOnly ? (
    <IconButton variant="ghost" aria-label="Chat History">
      <History className="size-4.5 text-gray-600" />
    </IconButton>
  ) : (
    <div>
      <PopoverButton
        variant="ghost"
        disabled={isCreatingConversation}
        className="disabled:opacity-50 disabled:cursor-not-allowed has-[>svg]:px-2 py-2"
        aria-label="Select conversation"
      >
        <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[13px] text-left text-nowrap">
          <p className="block leading-[16px] whitespace-pre">
            {currentConversation
              ? formatConversationLabel(currentConversation)
              : lastKnownLabelRef.current}
          </p>
        </div>
        <ChevronDown className="size-4 text-gray-500" />
      </PopoverButton>
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip delayDuration={700}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Analysis History</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-[300px] p-0 overflow-hidden" align="start">
        <div
          role="listbox"
          aria-label="Select a conversation"
          {...(focusedItemId ? { "aria-activedescendant": focusedItemId } : {})}
          onKeyDown={handleKeyDown}
          className="max-h-[450px] overflow-y-auto py-1 px-1 space-y-1"
        >
          {visibleShortcuts.length > 0 && (
            <>
              <div className="px-2 py-1">
                <h3 className="text-xs font-medium text-gray-500 tracking-wide">Featured</h3>
              </div>
              {visibleShortcuts.map((shortcut, index) => (
                <FeaturedConversationItem key={index} label={shortcut.label} url={shortcut.url} />
              ))}
              <div className="px-2 py-1">
                <h3 className="text-xs font-medium text-gray-500 tracking-wide">Recents</h3>
              </div>
            </>
          )}
          {conversations.length > 0 ? (
            conversations.map((conversation, index) => (
              <ConversationItem
                key={conversation.id}
                id={`conversation-selector-${conversation.id}`}
                conversation={conversation}
                isSelected={conversation.id === currentConversationId}
                isFocused={index === focusedIndex}
                isStreaming={
                  isStreamingProp?.(conversation.id) ??
                  conversationsContext?.isStreaming(conversation.id) ??
                  false
                }
                onSelect={handleConversationSelect}
                onDelete={onDelete}
                onRename={onRename}
                formatLabel={formatConversationLabel}
              />
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
              No conversations available
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
